import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1565035010268-a3816f98589a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjb25jZXJ0JTIwbGl2ZSUyMHN0cmVhbSUyMHBlcmZvcm1hbmNlfGVufDB8fHx8MTc4MjE2ODg5NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login({ email, password });
      toast.success(`Welcome back, ${u.name}`);
      navigate("/");
    } catch (err) {
      toast.error("Login failed", {
        description: err.detail?.detail || "Check your email and password.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="mb-10 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="mb-8">
            <Logo size={44} />
          </div>

          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
            Welcome back
          </h1>
          <p className="mt-2 text-muted-foreground">
            Log in to follow creators and save events.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-12 rounded-xl pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 rounded-xl pl-10"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="h-12 w-full rounded-full bg-brand text-base hover:bg-brand-hover"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New to WhooLive?{" "}
            <Link to="/register" className="font-semibold text-brand" data-testid="login-to-register">
              Create an account
            </Link>
          </p>
        </div>
      </div>

      {/* Visual */}
      <div className="relative hidden lg:block">
        <img src={SIDE_IMG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight">
            Every stream. Every event. One place.
          </h2>
          <p className="mt-3 max-w-md text-white/80">
            Join a global community discovering what's happening live, right now.
          </p>
        </div>
      </div>
    </div>
  );
}
